import {
  AdminApiError,
  type AdminClient,
  type GameCatalogEntry,
} from '@apx/admin-sdk';
import { skeletonTable } from '../skeleton.js';
import { showToast } from '../toast.js';
import { promptModal } from '../modal.js';

export interface GamesPageDeps {
  client: AdminClient;
  integratorId: string;
}

/** Preset lobby badges offered as toggle chips (admins pick, don't type). */
const BADGE_PRESETS: ReadonlyArray<string> = ['featured', 'new', 'hot', 'popular', 'exclusive'];

/**
 * Unified per-integrator games page. Each row covers everything an operator
 * does with a game: toggle enable/disable, set display order + badges
 * (lobby decoration), and drill into the per-game config (timing, RNG,
 * jackpot, rules, live inspector). Previously this was split across two
 * sidebar items (Games + Game catalog) — same data, two layouts — which
 * was just noise.
 */
export async function renderGamesIndexPage(
  root: HTMLElement,
  deps: GamesPageDeps,
): Promise<void> {
  root.innerHTML = `
    <div class="row" style="margin-top:0;justify-content:space-between;align-items:flex-start;gap:16px">
      <div>
        <h1 class="page-title">Catalog</h1>
        <p class="page-sub" style="margin-bottom:0">Which games this integrator exposes to players, plus lobby order + badges. Game configs (timing, RNG, jackpot, rules) are <strong>global</strong> — edit them under <a href="/games" style="color:var(--accent-2)">Provider → Games</a>.</p>
      </div>
      <span id="games-msg" class="muted" style="font-size:12px;white-space:nowrap;padding-top:6px"></span>
    </div>
    <div id="games-table" style="margin-top:20px">${skeletonTable({ rows: 3, columns: 5 })}</div>
  `;
  const tableEl = root.querySelector<HTMLElement>('#games-table')!;
  const msgEl = root.querySelector<HTMLElement>('#games-msg')!;

  function setMsg(text: string, cls: 'muted' | 'error' = 'muted'): void {
    msgEl.textContent = text;
    msgEl.className = cls;
  }

  async function refresh(): Promise<void> {
    try {
      const { games } = await deps.client.listGameCatalog(deps.integratorId);
      tableEl.innerHTML = renderTable(games);
      bindButtons();
    } catch (err) {
      tableEl.innerHTML = `<p class="error">${escapeHtml(describe(err))}</p>`;
    }
  }

  function renderTable(games: GameCatalogEntry[]): string {
    if (games.length === 0) {
      return '<div class="card"><p class="muted" style="margin:0">No games registered. Provider must register the game in the registry first.</p></div>';
    }
    // Lobby order, then name — matches how players see them.
    const sorted = [...games].sort(
      (a, b) =>
        (a.integrator?.displayOrder ?? 0) - (b.integrator?.displayOrder ?? 0) ||
        a.gameId.localeCompare(b.gameId),
    );
    return `<div class="catalog-list">${sorted.map(rowHtml).join('')}</div>`;
  }

  function rowHtml(g: GameCatalogEntry): string {
    const tEnabled = g.integrator?.enabled ?? false;
    const order = g.integrator?.displayOrder ?? 0;
    const badges = g.integrator?.badges ?? [];
    const type = (g.type || '').toLowerCase();
    // Config is global — drill into the provider-scope game detail, not a
    // per-integrator variant.
    const manageHref = `/games/${encodeURIComponent(g.gameId)}/timing`;
    // Predefined lobby badges as toggle chips (no manual typing). Any existing
    // custom badge not in the preset list is preserved as its own chip.
    const active = new Set(badges);
    const options = [...BADGE_PRESETS, ...badges.filter((bdg) => !BADGE_PRESETS.includes(bdg))];
    const badgeToggles = options
      .map(
        (bdg) =>
          `<button type="button" class="cat-badge-toggle${active.has(bdg) ? ' on' : ''}" data-badge="${escapeHtml(bdg)}">${escapeHtml(bdg)}</button>`,
      )
      .join('');
    return `<div class="catalog-row${tEnabled ? '' : ' is-off'}">
      <div class="catalog-game">
        ${gameAvatar(g.gameId, type)}
        <div class="catalog-id">
          <div class="catalog-name">${escapeHtml(g.gameId)}</div>
          <div class="catalog-meta">
            <span class="cat-type">${escapeHtml(g.type)}</span>
            <span class="cat-sep">·</span>
            <span class="cat-reg ${g.registryEnabled ? 'ok' : 'bad'}">${g.registryEnabled ? 'registry on' : 'registry off'}</span>
            ${g.registryMaintenance.enabled ? '<span class="cat-sep">·</span><span class="cat-reg bad">maintenance</span>' : ''}
            <span class="cat-sep">·</span>
            <span class="muted">${escapeHtml(g.scope)}</span>
          </div>
        </div>
        <span class="pill ${tEnabled ? 'live' : 'warn'} catalog-state">${tEnabled ? 'enabled' : 'disabled'}</span>
      </div>

      <label class="cat-field">
        <span class="cat-label">Order</span>
        <input data-order="${g.gameId}" type="number" min="0" max="9999" value="${order}" class="cat-input cat-order" />
      </label>

      <div class="cat-field cat-field-badges">
        <span class="cat-label">Badges</span>
        <div class="cat-badges" data-badges-for="${g.gameId}">${badgeToggles}<button type="button" class="cat-badge-add" data-badges-add="${g.gameId}">+ Add</button></div>
      </div>

      <div class="catalog-actions">
        ${
          !tEnabled && !g.registryEnabled
            ? `<button class="ghost cat-toggle" disabled title="Disabled by provider — re-enable under Provider → Games first">Enable</button>`
            : `<button data-toggle="${g.gameId}" data-current="${tEnabled}" class="${tEnabled ? 'ghost' : 'primary'} cat-toggle">${tEnabled ? 'Disable' : 'Enable'}</button>`
        }
        <button data-save="${g.gameId}" class="ghost">Save</button>
        ${
          tEnabled
            ? `<a href="${manageHref}" class="btn-link">Manage →</a>`
            : // Disabled games have nothing to manage, but each row is its own
              // grid — dropping the link outright shrinks the actions column and
              // knocks this row out of line with the others.
              `<span class="btn-link cat-manage-spacer" aria-hidden="true">Manage →</span>`
        }
      </div>
    </div>`;
  }


  /**
   * Real game art where we ship it, monogram where we do not.
   *
   * The monogram alone was ambiguous — sicbo and singaporelottery both render
   * "SI" — and gave no quick way to pick a game out of a list. The <img> sits
   * ON TOP of the monogram and removes itself when the file is missing, so a
   * game we ship no icon for degrades to the old letters rather than showing
   * a broken image.
   */
  function gameAvatar(gameId: string, type: string): string {
    const safeId = gameId.replace(/[^a-z0-9-]/gi, '');
    return `<span class="game-avatar" data-type="${escapeHtml(type)}">${escapeHtml(monogram(gameId))}<img class="game-avatar-img" src="/game-icons/${safeId}.png" alt="" loading="lazy" onerror="this.remove()"></span>`;
  }
  /** Two-letter monogram for the game avatar (e.g. "dragontiger" → "DR"). */
  function monogram(gameId: string): string {
    return gameId.replace(/[^a-z0-9]/gi, '').slice(0, 2).toUpperCase() || '?';
  }

  function bindButtons(): void {
    // Badge chips toggle on click — no manual typing for the presets.
    tableEl.querySelectorAll<HTMLButtonElement>('.cat-badge-toggle').forEach((btn) => {
      btn.addEventListener('click', () => btn.classList.toggle('on'));
    });
    // "+ Add" lets an operator add a custom tag (badges are free-form on the
    // backend; the integrator's lobby decides how to render each one).
    tableEl.querySelectorAll<HTMLButtonElement>('.cat-badge-add').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.badgesAdd!;
        const raw = await promptModal({
          title: 'Add a badge',
          body: 'A lobby tag for this game. Lowercase, max 40 chars.',
          placeholder: 'e.g. jackpot',
          confirmLabel: 'Add',
        });
        const val = (raw ?? '').trim().toLowerCase();
        if (!val) return;
        if (val.length > 40) {
          setMsg('Badge max 40 chars.', 'error');
          return;
        }
        const container = tableEl.querySelector<HTMLElement>(`[data-badges-for="${id}"]`);
        if (!container) return;
        const exists = Array.from(container.querySelectorAll<HTMLElement>('.cat-badge-toggle')).some(
          (c) => (c.dataset.badge ?? '') === val,
        );
        if (exists) {
          container.querySelector<HTMLElement>(`.cat-badge-toggle[data-badge="${CSS.escape(val)}"]`)?.classList.add('on');
          return;
        }
        const chip = document.createElement('button');
        chip.type = 'button';
        chip.className = 'cat-badge-toggle on';
        chip.dataset.badge = val;
        chip.textContent = val;
        chip.addEventListener('click', () => chip.classList.toggle('on'));
        container.insertBefore(chip, btn);
      });
    });
    tableEl.querySelectorAll<HTMLButtonElement>('button[data-toggle]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.toggle!;
        const current = btn.dataset.current === 'true';
        try {
          await deps.client.patchGameCatalog(deps.integratorId, id, { enabled: !current });
          showToast(`${id} ${!current ? 'enabled' : 'disabled'}.`, 'success');
          await refresh();
        } catch (err) {
          setMsg(describe(err), 'error');
        }
      });
    });
    tableEl.querySelectorAll<HTMLButtonElement>('button[data-save]').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const id = btn.dataset.save!;
        const orderRaw = tableEl.querySelector<HTMLInputElement>(`input[data-order="${id}"]`)?.value ?? '';
        const order = Number(orderRaw);
        if (!Number.isFinite(order) || order < 0 || order > 9999) {
          setMsg(`Order must be 0–9999 for ${id}.`, 'error');
          return;
        }
        const badges = Array.from(
          tableEl.querySelectorAll<HTMLButtonElement>(
            `[data-badges-for="${id}"] .cat-badge-toggle.on`,
          ),
        )
          .map((b) => b.dataset.badge ?? '')
          .filter((s) => s.length > 0);
        try {
          await deps.client.patchGameCatalog(deps.integratorId, id, { displayOrder: order, badges });
          showToast(`${id} saved.`, 'success');
          await refresh();
        } catch (err) {
          setMsg(describe(err), 'error');
        }
      });
    });
  }

  await refresh();
}

function describe(err: unknown): string {
  if (err instanceof AdminApiError) return `${err.code}: ${err.message}`;
  return err instanceof Error ? err.message : String(err);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}
